import {
  toWSDateString,
  WSElementColorsType
} from "@wingspanhq/fe-component-library";
import {
  PayeeRequirement,
  PayeeRequirementPayerOverrideStatus,
  RequirementStatus
} from "@wingspanhq/wingspan-api-sdk";

import { selectorIsRequirementArchived } from "../selectors/selectorIsRequirementArchived";
import { selectorIsRequirementExpired } from "../selectors/selectorIsRequirementExpired";
import { selectorIsRequirementExpiringInDaysFormatted } from "../selectors/selectorIsRequirementExpiringInDaysFormatted";
import { selectorIsRequirementExpiringSoon } from "../selectors/selectorIsRequirementExpiringSoon";
import { selectorIsRequirementPostApproval } from "../selectors/selectorIsRequirementPostApproval";
import { RequirementContextType } from "../types";

export const getStatusSecondaryTextPrefix = (
  requirement: PayeeRequirement,
  requirementContextType: RequirementContextType
): string => {
  // NOTE: a payer override on an EmployeeTaxForms requirement is sticky
  // (applyPayerOverrides) even while dataSourceStatus stays Complete, so the
  // "Marked incomplete by …" note must surface like any other override.
  switch (requirement.payerOverrideStatus) {
    case PayeeRequirementPayerOverrideStatus.COMPLETED:
      return requirementContextType === RequirementContextType.Payee
        ? "Marked complete by you"
        : "Marked complete by client";
    case PayeeRequirementPayerOverrideStatus.PENDING_COMPLETION:
      return requirementContextType === RequirementContextType.Payee
        ? "Marked incomplete by you"
        : "Marked incomplete by client";
    default:
      return "";
  }
};

export const extendRequirementStatusSecondaryTextProps = (
  requirement: PayeeRequirement,
  requirementContextType: RequirementContextType,
  originalRequirementStatusText: string | undefined
): {
  secondaryText: string | undefined;
  secondaryTextColor: WSElementColorsType | undefined;
} => {
  const prefix = getStatusSecondaryTextPrefix(
    requirement,
    requirementContextType
  );

  const isReadyForApprove =
    requirement.status === RequirementStatus.PENDING_PAYER_REVIEW;

  const isPostApproval = selectorIsRequirementPostApproval(requirement);

  const isArchived = selectorIsRequirementArchived(requirement);

  if (isArchived) {
    return {
      secondaryText: "Requirement is inactive",
      secondaryTextColor: "gray400"
    };
  }

  let defaultText = "";

  if (isReadyForApprove) {
    if (requirementContextType === RequirementContextType.Payee) {
      defaultText = "Awaiting your approval";
    } else {
      defaultText = "Awaiting client approval";
    }
  } else if (isPostApproval) {
    if (requirementContextType === RequirementContextType.Payee) {
      defaultText =
        requirement.status === RequirementStatus.COMPLETED
          ? "Approved by you"
          : "Rejected by you";
    } else {
      defaultText =
        requirement.status === RequirementStatus.COMPLETED
          ? "Approved by client"
          : "Rejected by client";
    }
  } else if (requirement.payerOverrideStatus) {
    defaultText = prefix;
  }

  const mainText = defaultText || originalRequirementStatusText || "";

  const now = new Date();

  const effectiveText =
    requirement.effectiveDate &&
    requirement.effectiveDate > now &&
    requirementContextType === RequirementContextType.Payee
      ? `Takes effect ${toWSDateString(requirement.effectiveDate, "input")}`
      : null;

  const expirationText = selectorIsRequirementExpired(requirement)
    ? "Expired"
    : selectorIsRequirementExpiringSoon(requirement)
      ? `Expiring ${selectorIsRequirementExpiringInDaysFormatted(requirement)}`
      : null;

  const secondaryText = [effectiveText || expirationText, mainText]
    .filter(Boolean)
    .join(" • ");

  const secondaryTextColor =
    requirement.status === RequirementStatus.COMPLETED
      ? undefined
      : selectorIsRequirementExpired(requirement)
        ? "red300"
        : selectorIsRequirementExpiringSoon(requirement) ||
            (isReadyForApprove &&
              requirementContextType === RequirementContextType.Payee)
          ? "amber400"
          : undefined;

  return { secondaryText, secondaryTextColor };
};
